/**
 * TILT — Normal-like survival controlled by device orientation (mobile/tablet).
 * Cursor behaves like a ball on a tilted table: acceleration, inertia, soft damping.
 */

export const TILT_MOBILE_ONLY_MSG = "This mode is for Mobile Only";
export const TILT_BALLS_PER_SPAWN = 25;
export const TILT_SCORE_PER_SPAWN = 500;
export const TILT_BALL_SPEED = 0.8;

/** Low-pass on raw orientation (°). Higher = snappier, lower = smoother. */
const ORIENT_FILTER = 0.14;
/** Degrees of tilt ignored around the calibrated rest pose. */
const DEADZONE_DEG = 2.8;
/** Soft ramp after deadzone before full gravity response (°). */
const RESPONSE_SOFT_DEG = 10;
/** Gravity-scale accel (px/s² per “unit” tilt after soft response). */
const GRAVITY = 3200;
/** Velocity damping (1/s) — natural slowdown when the device is flat. */
const DRAG = 2.35;
/** Soft speed cap (px/s). */
const MAX_SPEED = 980;
/** Edge bounce energy retained. */
const WALL_RESTITUTION = 0.42;

type OrientSample = { beta: number; gamma: number };

let listening = false;
let orientHandler: ((e: DeviceOrientationEvent) => void) | null = null;
let raw: OrientSample = { beta: 0, gamma: 0 };
let filtered: OrientSample = { beta: 0, gamma: 0 };
let baseline: OrientSample | null = null;
let baselineSamples = 0;
let vx = 0;
let vy = 0;
let posX = 0;
let posY = 0;
let hasSample = false;

export function deviceHasMotionSensors(): boolean {
    return typeof window !== "undefined" && typeof window.DeviceOrientationEvent !== "undefined";
}

/** Phones, tablets, and iPad (including desktop-UA iPadOS). */
export function isTiltDeviceEligible(isTouchUi: () => boolean): boolean {
    if (typeof window === "undefined" || typeof navigator === "undefined") return false;
    if (isTouchUi()) return true;
    if (document.documentElement.classList.contains("touch-ipad")) return true;
    if (/iPad|iPhone|iPod/i.test(navigator.userAgent)) return true;
    // iPadOS 13+ often reports as MacIntel with multitouch
    if (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1) return true;
    if (navigator.maxTouchPoints > 0 && matchMedia("(pointer: coarse)").matches) return true;
    if (navigator.maxTouchPoints > 0 && matchMedia("(hover: none)").matches) return true;
    return false;
}

/** iOS 13+ requires a user-gesture permission grant (must not be deferred by timers). */
export async function requestTiltOrientationPermission(): Promise<boolean> {
    if (!deviceHasMotionSensors()) return false;
    const DOE = window.DeviceOrientationEvent as typeof DeviceOrientationEvent & {
        requestPermission?: () => Promise<"granted" | "denied" | "default">;
    };
    if (typeof DOE.requestPermission === "function") {
        try {
            const result = await DOE.requestPermission();
            return result === "granted";
        } catch {
            return false;
        }
    }
    // No permission API — sensors may still work after listening starts.
    return true;
}

function softTiltResponse(deltaDeg: number): number {
    const a = Math.abs(deltaDeg);
    if (a <= DEADZONE_DEG) return 0;
    const signed = deltaDeg < 0 ? -1 : 1;
    const t = Math.min(1, (a - DEADZONE_DEG) / RESPONSE_SOFT_DEG);
    // Smoothstep for gentle takeoff
    const eased = t * t * (3 - 2 * t);
    return signed * eased * Math.min(1, a / 28);
}

function onOrient(e: DeviceOrientationEvent): void {
    if (e.beta == null || e.gamma == null) return;
    raw.beta = e.beta;
    raw.gamma = e.gamma;
    if (!hasSample) {
        filtered.beta = e.beta;
        filtered.gamma = e.gamma;
        hasSample = true;
    } else {
        filtered.beta += (e.beta - filtered.beta) * ORIENT_FILTER;
        filtered.gamma += (e.gamma - filtered.gamma) * ORIENT_FILTER;
    }
    if (baseline == null) {
        baselineSamples += 1;
        if (baselineSamples >= 8) {
            baseline = { beta: filtered.beta, gamma: filtered.gamma };
        }
    }
}

export function startTiltOrientationListening(): void {
    if (listening || typeof window === "undefined") return;
    orientHandler = onOrient;
    window.addEventListener("deviceorientation", orientHandler, { passive: true });
    listening = true;
}

export function stopTiltOrientationListening(): void {
    if (!listening || !orientHandler) return;
    window.removeEventListener("deviceorientation", orientHandler);
    orientHandler = null;
    listening = false;
}

export function resetTiltPhysics(x: number, y: number): void {
    vx = 0;
    vy = 0;
    posX = x;
    posY = y;
    baseline = null;
    baselineSamples = 0;
    hasSample = false;
    raw = { beta: 0, gamma: 0 };
    filtered = { beta: 0, gamma: 0 };
}

export function getTiltCursorPos(): { x: number; y: number } {
    return { x: posX, y: posY };
}

export function syncTiltCursorPos(x: number, y: number): void {
    posX = x;
    posY = y;
}

/**
 * Integrate tilt physics for one frame.
 * Tilt left/right → γ; tilt forward (top away) → β negative relative → move up.
 */
export function tickTiltPhysics(
    dt: number,
    clampXY: (x: number, y: number) => { x: number; y: number },
): { x: number; y: number } {
    const t = Math.max(0, Math.min(0.05, dt));
    if (baseline && hasSample) {
        const gx = softTiltResponse(filtered.gamma - baseline.gamma);
        // Forward (device top away) decreases beta from upright/flat rest → ball rolls “up” the screen.
        const gy = softTiltResponse(-(filtered.beta - baseline.beta));
        vx += gx * GRAVITY * t;
        vy += gy * GRAVITY * t;
    }

    const damp = Math.exp(-DRAG * t);
    vx *= damp;
    vy *= damp;

    const spd = Math.hypot(vx, vy);
    if (spd > MAX_SPEED) {
        const s = MAX_SPEED / spd;
        vx *= s;
        vy *= s;
    }

    posX += vx * t;
    posY += vy * t;

    const c = clampXY(posX, posY);
    if (c.x !== posX) {
        posX = c.x;
        vx *= -WALL_RESTITUTION;
        if (Math.abs(vx) < 12) vx = 0;
    }
    if (c.y !== posY) {
        posY = c.y;
        vy *= -WALL_RESTITUTION;
        if (Math.abs(vy) < 12) vy = 0;
    }
    return { x: posX, y: posY };
}

let mobileOnlyToastTimer: ReturnType<typeof setTimeout> | null = null;

/** Brief on-screen notice when TILT is started without motion sensors / on PC. */
export function showTiltMobileOnlyToast(): void {
    if (typeof document === "undefined") return;
    let el = document.getElementById("tiltMobileOnlyToast");
    if (!el) {
        el = document.createElement("div");
        el.id = "tiltMobileOnlyToast";
        el.className = "tiltMobileOnlyToast";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        document.body.appendChild(el);
    }
    el.textContent = TILT_MOBILE_ONLY_MSG;
    el.classList.add("tiltMobileOnlyToast--visible");
    if (mobileOnlyToastTimer) clearTimeout(mobileOnlyToastTimer);
    mobileOnlyToastTimer = setTimeout(() => {
        el?.classList.remove("tiltMobileOnlyToast--visible");
        mobileOnlyToastTimer = null;
    }, 2600);
}
